/**
 * Décodage du QR « commande serveur » présenté en caisse (mode restaurant).
 *
 * Produit par la PWA /serveur (pages/serveur/qrcommande.vue, payload v2). À la
 * différence du panier Scan & Go supermarché (scangopanier.js), il ne porte pas
 * de code société/magasin mais un discriminant `k:"cmd"` et un identifiant
 * stable de commande - c'est lui qui permet à la caisse de reconnaître une mise
 * à jour de la MÊME commande (le serveur a ajouté des plats) plutôt qu'une
 * nouvelle, et donc de n'ingérer que le delta.
 *
 * Forme du payload :
 *   {
 *     v: 2, k: "cmd",
 *     m: "svc" | "pay",          // envoi cuisine | paiement
 *     id: "<pidServeur>-<cléCommande>",
 *     s:  "<pid serveur>",  sn: "<nom serveur>",
 *     t:  "<pid table>",    tn: "<nom table>",
 *     n:  "W-JJMM-NNN",           // n° d'attente
 *     l:  [[ean, tarifPid, qte, qteEnvoyee, accompagnement, note, ingredients?], ...]
 *     p:  [rang, total, tampon]   // seulement si la commande est en plusieurs QR
 *   }
 *
 * `ingredients` (7e élément, facultatif) : « rxxxxxx,dyyyyyy », un code court par
 * ingrédient retiré (r) ou doublé (d). La caisse le retrouve dans sa propre liste.
 *
 * Commande en plusieurs QR : chaque partie porte `p`. Seule la partie 1 porte
 * l'en-tête (id, serveur, table, numéro) ; les suivantes n'ont que `p` et leurs
 * lignes. Le tampon rattache les parties d'une même version de la commande.
 * La caisse les regroupe avec fusionnerParties avant tout traitement.
 *
 * Comme pour le panier : ni prix ni libellé. La caisse recalcule tout depuis son
 * propre catalogue (barcodeIndex + tarifsDepuisArticle), elle seule fait foi.
 */

const nettoyer = (v) => String(v === null || v === undefined ? "" : v).trim();

/**
 * @param {string} txt texte brut (webcam, téléphone lié ou douchette)
 * @returns {object|null} payload normalisé, ou null si ce n'est pas un QR commande
 */
export const parsePayloadCommandeServeur = (txt) => {
    const s = String(txt || "").trim();
    /* Garde bon marché : évite un JSON.parse sur chaque code-barres scanné. */
    if (s.length < 2 || s.charAt(0) !== "{") return null;

    let o;
    try {
        o = JSON.parse(s);
    } catch (e) {
        return null;
    }

    if (!o || typeof o !== "object" || Array.isArray(o)) return null;
    /* Le discriminant : c'est ce qui distingue une commande serveur d'un panier
       supermarché (lequel n'a pas de `k` et porte `mag`). */
    if (o.k !== "cmd") return null;
    if (!Array.isArray(o.l) || o.l.length === 0) return null;

    /* Partie d'une commande en plusieurs QR. Un total de 1 n'est pas un
       découpage : la commande se traite alors comme un QR ordinaire. */
    let partie = null;
    if (Array.isArray(o.p) && o.p.length >= 3) {
        const rang = parseInt(o.p[0], 10);
        const total = parseInt(o.p[1], 10);
        const tampon = nettoyer(o.p[2]);
        if (!(rang >= 1) || !(total >= rang) || tampon === "") return null;
        if (total > 1) partie = { rang, total, tampon };
    }

    // Seule la partie 1 porte l'identifiant ; un QR ordinaire aussi.
    if (!o.id && !(partie && partie.rang > 1)) return null;

    const lignes = [];
    for (const entree of o.l) {
        if (!Array.isArray(entree) || entree.length < 4) return null;
        const ean = nettoyer(entree[0]);
        const tarifPid = nettoyer(entree[1]);
        const qte = parseFloat(entree[2]);
        const qteEnvoyee = parseFloat(entree[3]);
        // Une ligne sans ean ni tarif n'est pas résoluble côté caisse.
        if (ean === "" || tarifPid === "") return null;
        if (!isFinite(qte) || qte < 0) return null;
        lignes.push({
            ean,
            tarifPid,
            qte,
            qteEnvoyee: isFinite(qteEnvoyee) ? qteEnvoyee : 0,
            accompagnement: nettoyer(entree[4]),
            note: nettoyer(entree[5]),
            ingredients: nettoyer(entree[6]),
        });
    }

    return {
        partie,
        v: parseInt(o.v, 10) || 2,
        mode: o.m === "pay" ? "paiement" : "service",
        id: nettoyer(o.id),
        serveur: { pid: nettoyer(o.s), name: nettoyer(o.sn) },
        table: { pid: nettoyer(o.t), name: nettoyer(o.tn) },
        // Libellé du client sur la table (« Client 2 », « Dame en rouge »…) :
        // plusieurs clients indépendants peuvent partager une même table.
        client: nettoyer(o.cl),
        numero: nettoyer(o.n),
        lignes,
    };
};

/**
 * Regroupe les parties lues d'une commande en plusieurs QR.
 *
 * @param {object[]} parties payloads normalisés (parsePayloadCommandeServeur) d'un même tampon
 * @returns {{cmd: object|null, total: number, recues: number[], manquantes: number[], complete: boolean}}
 *          `cmd` est la commande regroupée (lignes dans l'ordre des parties), ou
 *          null tant que la partie 1 n'est pas lue : elle seule porte l'en-tête.
 */
export const fusionnerParties = (parties) => {
    const lues = (parties || []).filter((c) => c && c.partie)
        .sort((a, b) => a.partie.rang - b.partie.rang);
    const total = lues.length ? lues[0].partie.total : 0;
    const recues = lues.map((c) => c.partie.rang);
    const manquantes = [];
    for (let r = 1; r <= total; r++) if (recues.indexOf(r) === -1) manquantes.push(r);

    const tete = lues.find((c) => c.partie.rang === 1) || null;
    const cmd = tete
        ? Object.assign({}, tete, {
            partie: null,
            lignes: lues.reduce((l, c) => l.concat(c.lignes), []),
        })
        : null;

    return { cmd, total, recues, manquantes, complete: total > 0 && manquantes.length === 0 };
};
